import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import type { IClientUser } from '../../interfaces/user';
import Navigation from '../../components/Navigation';
import { GET_ADMIN_USER_ANALYTICS } from '../../graphql/adminAnalytics';

const AdminDashboard: React.FC<IClientUser> = (props) => {
  const { data, loading, error } = useQuery(GET_ADMIN_USER_ANALYTICS);
  const analytics = data?.getAdminUserAnalytics;

  const getPercentage = (part: number, total: number) => {
    if (!total) return '0%';
    return Math.round((part / total) * 100) + '%';
  };

  const total = analytics?.totalUsers || 0;

  const statGroups = [
    {
      title: 'Overview',
      stats: [
        { label: 'Total Users', value: loading ? '...' : total, subtitle: 'All registered accounts' },
        { label: 'Brands', value: loading ? '...' : analytics?.totalBrands || 0, subtitle: getPercentage(analytics?.totalBrands, total) + ' of total' },
        { label: 'Creators', value: loading ? '...' : analytics?.totalCreators || 0, subtitle: getPercentage(analytics?.totalCreators, total) + ' of total' }
      ]
    },
    {
      title: 'Onboarding Funnel',
      stats: [
        { label: 'Completed Onboarding', value: loading ? '...' : analytics?.onboardedUsers || 0, subtitle: getPercentage(analytics?.onboardedUsers, total) + ' completion rate' },
        { label: 'Onboarding Drop-offs', value: loading ? '...' : analytics?.dropOffUsers || 0, subtitle: 'Verified, not onboarded' },
        { label: 'Failed Sign-ups', value: loading ? '...' : analytics?.failedSignups || 0, subtitle: 'Unverified emails' }
      ]
    },
    {
      title: 'Activity',
      stats: [
        { label: 'Active Projects', value: '89', subtitle: '↗ +8%', isSuccess: true },
        { label: 'Coffee Matches', value: '156', subtitle: '↗ +23%', isSuccess: true },
        { label: 'System Health', value: '98%', subtitle: '↗ +2%', isSuccess: true }
      ]
    }
  ];

  const quickActions = [
    {
      title: 'Users',
      subtitle: 'Manage user accounts',
      path: '/admin/users'
    },
    {
      title: 'Projects',
      subtitle: 'View all projects',
      path: '/admin/projects'
    },
    {
      title: 'Coffee Chats',
      subtitle: 'View chat matches',
      path: '/admin/coffee-chats'
    },
    {
      title: 'Settings',
      subtitle: 'System configuration',
      path: '/admin/settings'
    }
  ];

  return (
    <main className="page-admin-dashboard">
      <Fragment>
        <Navigation {...props} />
        <section className="container">
          <div className="admin-users-container">
            {/* Header Section */}
            <div className="admin-header" style={{ marginBottom: '2rem' }}>
              <div className="admin-header-content">
                <h1 className="admin-title" style={{ color: '#2c3e50' }}>Dashboard</h1>
                <p className="admin-subtitle" style={{ color: '#6c757d' }}>Welcome to MatchHat Admin Panel</p>
              </div>
            </div>

            {error && (
              <div className="notification is-danger">
                Error loading analytics data.
              </div>
            )}

            {/* Stats Section */}
            <div style={{ marginBottom: '3rem' }}>
              {statGroups.map((group, groupIndex) => (
                <div key={groupIndex} style={{ marginBottom: '2rem' }}>
                  <h2 className="title is-5" style={{ marginBottom: '1rem', color: '#2c3e50' }}>
                    {group.title}
                  </h2>
                  <div className="columns is-multiline">
                    {group.stats.map((stat, index) => (
                      <div key={index} className="column is-6-tablet is-4-desktop">
                        <div className="box" style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #f0f0f0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                          borderRadius: '8px',
                          height: '100%',
                          padding: '1.5rem'
                        }}>
                          <p className="heading" style={{ letterSpacing: '1px', marginBottom: '0.5rem', color: '#6c757d' }}>{stat.label}</p>
                          <p className="title is-3 has-text-dark" style={{ marginBottom: '0.5rem' }}>{stat.value}</p>
                          {stat.subtitle && (
                            <p className={`has-text-weight-semibold is-size-7 ${stat.isSuccess ? 'has-text-success' : ''}`} style={{ color: stat.isSuccess ? undefined : '#8f9bb3' }}>
                              {stat.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions Section */}
            <div className="box">
              <h2 className="title is-5" style={{ marginBottom: '1.5rem', color: '#2c3e50' }}>Quick Actions</h2>
              <div className="columns is-multiline">
                {quickActions.map((action, index) => (
                  <div key={index} className="column is-6-tablet is-3-desktop">
                    <div className="box" style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      height: '120px',
                      padding: 0,
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#7C87FF';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 135, 255, 0.25)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#f0f0f0';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      <Link to={action.path} style={{
                        display: 'flex',
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%',
                        textAlign: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '1.25rem'
                      }}>
                        <h3 className="is-size-5 has-text-weight-bold" style={{ color: '#2c3e50', marginBottom: '0.25rem' }}>
                          {action.title}
                        </h3>
                        <p className="is-size-7" style={{ color: '#6c757d', marginTop: '0.25rem' }}>
                          {action.subtitle}
                        </p>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </Fragment>
    </main>
  );
};

export default AdminDashboard;